import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { useQuery } from "@tanstack/react-query";
import {
  ArrowRight, ArrowUpRight, ShieldCheck, CheckCircle2, Sparkles,
  Star, MessageCircle, CalendarCheck, Clock, BadgeCheck
} from "lucide-react";
import { api, pick, waLink } from "../lib/api";
import { useLang } from "../lib/i18n";
import { Reveal, MaskedLine, Eyebrow, SectionTitle, FadeIn } from "../components/Reveal";
import Marquee from "../components/Marquee";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "../components/ui/accordion";
import { Phone } from "lucide-react";
import { PHONE_TEL } from "../lib/api";

const HERO_IMG = "https://images.unsplash.com/photo-1566836610593-62a64888a216?crop=entropy&cs=srgb&fm=jpg&q=85";
const fmt = (n) => `Rs ${Number(n).toLocaleString("en-MU")}`;

const Hero = () => {
  const { t } = useLang();
  const { scrollY } = useScroll();
  const y = useTransform(scrollY, [0, 700], [0, 90]);
  return (
    <section className="relative min-h-screen flex items-center pt-[96px] pb-16 overflow-hidden" data-testid="hero-section">
      <div className="absolute top-0 right-0 w-[45vw] h-[45vw] bg-mint rounded-full blur-3xl opacity-70 -translate-y-1/3 translate-x-1/4 pointer-events-none" />
      <div className="max-w-7xl mx-auto px-6 md:px-10 grid lg:grid-cols-12 gap-12 lg:gap-8 items-center w-full">
        <div className="lg:col-span-6">
          <FadeIn delay={0.1}>
            <div className="inline-flex items-center gap-2 rounded-full border border-forest-200 bg-white/80 backdrop-blur px-4 py-2 text-xs font-semibold text-forest-800" data-testid="hero-trust-badge">
              <ShieldCheck size={15} className="text-forest-600" />
              {t.hero.eyebrow}
            </div>
          </FadeIn>
          <h1 className="mt-7 font-display tracking-tighter leading-[0.95] text-forest-950">
            <MaskedLine delay={0.25} className="text-[13vw] sm:text-6xl lg:text-7xl xl:text-[5.2rem] font-light">{t.hero.line1}</MaskedLine>
            <MaskedLine delay={0.4} className="text-[13vw] sm:text-6xl lg:text-7xl xl:text-[5.2rem] font-extrabold">
              <span className="text-forest-700">{t.hero.line2}</span>
            </MaskedLine>
          </h1>
          <FadeIn delay={0.7}>
            <p className="mt-6 text-body text-lg leading-relaxed max-w-xl">{t.hero.sub}</p>
          </FadeIn>
          <FadeIn delay={0.85}>
            <div className="mt-9 flex flex-wrap items-center gap-4">
              <Link to="/booking" data-testid="hero-book-btn" className="group inline-flex items-center gap-2.5 rounded-full bg-forest-700 px-7 py-4 text-base font-semibold text-white shadow-lg shadow-forest-700/25 hover:bg-forest-800 hover:shadow-xl hover:-translate-y-0.5 transition-all duration-300">
                <CalendarCheck size={18} />
                {t.hero.cta1}
                <ArrowRight size={17} className="transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
              <Link to="/quote" data-testid="hero-quote-btn" className="inline-flex items-center gap-2 rounded-full border border-forest-200 bg-white/70 backdrop-blur px-7 py-4 text-base font-semibold text-forest-900 hover:border-forest-400 hover:bg-white transition-all duration-300">
                {t.hero.cta2}
              </Link>
              <a href={waLink(t.waMsg.simple.en)} target="_blank" rel="noopener noreferrer" data-testid="hero-whatsapp-btn" className="inline-flex items-center gap-2 text-sm font-semibold text-forest-800 hover:text-forest-600 transition-colors">
                <MessageCircle size={17} className="text-[#25D366]" /> {t.hero.wa}
              </a>
            </div>
          </FadeIn>
          <FadeIn delay={1}>
            <div className="mt-12 flex flex-wrap gap-x-10 gap-y-4">
              {[t.hero.stat1, t.hero.stat2, t.hero.stat3].map((s, i) => (
                <div key={i} className="flex items-center gap-2.5 text-sm font-semibold text-forest-900">
                  <CheckCircle2 size={17} className="text-forest-500" /> {s}
                </div>
              ))}
            </div>
          </FadeIn>
        </div>
        <div className="lg:col-span-6 relative">
          <motion.div style={{ y }} className="relative">
            <div className="hero-img-reveal relative overflow-hidden rounded-[32px] shadow-2xl shadow-forest-900/20">
              <img src={HERO_IMG} alt="Bright, fresh home interior cared for by X Terminate" className="w-full h-[420px] md:h-[540px] object-cover" data-testid="hero-image" />
              <div className="absolute inset-0 bg-forest-950/5" />
            </div>
            <FadeIn delay={1.15} className="absolute -top-5 -left-3 sm:-left-6">
              <div className="animate-float-slow flex items-center gap-3 rounded-2xl bg-white/90 backdrop-blur-xl border border-forest-100 px-5 py-4 shadow-xl shadow-forest-900/10" data-testid="hero-chip-guarantee">
                <div className="w-10 h-10 rounded-xl bg-mint flex items-center justify-center">
                  <Sparkles size={19} className="text-forest-700" />
                </div>
                <div className="text-sm font-bold text-forest-900 leading-tight max-w-[180px]">{t.hero.chip1}</div>
              </div>
            </FadeIn>
            <FadeIn delay={1.3} className="absolute -bottom-6 right-4 sm:right-10">
              <div className="flex items-center gap-3 rounded-2xl bg-forest-900/95 backdrop-blur px-5 py-4 shadow-xl" data-testid="hero-chip-brn">
                <ShieldCheck size={22} className="text-forest-300" />
                <div className="text-sm leading-tight text-white font-semibold">
                  {t.hero.badge}
                  <div className="text-forest-300 text-xs font-normal tracking-wider mt-0.5">BRN C22188258</div>
                </div>
              </div>
            </FadeIn>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

const BentoCard = ({ s, i, lang, t }) => {
  const spans = [
    "md:col-span-7 md:row-span-2", "md:col-span-5 md:row-span-1", "md:col-span-5 md:row-span-1",
    "md:col-span-5 md:row-span-2", "md:col-span-7 md:row-span-1", "md:col-span-7 md:row-span-1",
  ][i % 6];
  return (
    <Reveal delay={0.06 * i} className={`col-span-1 ${spans}`}>
      <Link
        to={`/services/${s.slug}`}
        data-testid={`service-card-${s.slug}`}
        className="group relative block h-full min-h-[190px] overflow-hidden rounded-3xl bg-forest-900"
      >
        <img src={s.image} alt={pick(s, "name", lang)} className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-105" loading="lazy" />
        <div className="absolute inset-0 bg-gradient-to-t from-forest-950/90 via-forest-950/35 to-forest-950/5" />
        <div className="absolute inset-x-0 bottom-0 p-6 flex items-end justify-between gap-4">
          <div className="text-white">
            <h3 className="font-display text-xl sm:text-2xl font-semibold tracking-tight">{pick(s, "name", lang)}</h3>
            <p className="text-forest-100/85 text-sm mt-1 max-w-[240px]">{pick(s, "tagline", lang)}</p>
            <div className="mt-3 inline-flex items-center gap-2 rounded-full bg-white/15 backdrop-blur px-3.5 py-1.5 text-xs font-semibold">
              {t.services.from} {fmt(s.full_price)} · {pick(s, "unit", lang)}
            </div>
          </div>
          <div className="shrink-0 w-11 h-11 rounded-full bg-white/15 backdrop-blur flex items-center justify-center text-white transition-all duration-300 group-hover:bg-white group-hover:text-forest-800 group-hover:translate-x-1 group-hover:-translate-y-1">
            <ArrowUpRight size={19} />
          </div>
        </div>
      </Link>
    </Reveal>
  );
};

const ServicesBento = () => {
  const { t, lang } = useLang();
  const { data: services = [] } = useQuery({ queryKey: ["services"], queryFn: () => api.get("/services").then(r => r.data) });
  return (
    <section id="services" className="py-24 md:py-32 scroll-mt-16" data-testid="services-section">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <SectionTitle eyebrow={t.services.eyebrow} title={t.services.title} sub={t.services.sub} />
        <div className="mt-14 grid grid-cols-1 md:grid-cols-12 md:auto-rows-[195px] gap-5">
          {services.map((s, i) => <BentoCard key={s.slug} s={s} i={i} lang={lang} t={t} />)}
        </div>
      </div>
    </section>
  );
};

const GuaranteeBand = () => {
  const { t } = useLang();
  return (
    <section className="bg-forest-950 relative overflow-hidden" data-testid="guarantee-section">
      <ShieldCheck size={420} className="absolute -right-24 -bottom-24 text-forest-900/60 pointer-events-none" strokeWidth={0.6} />
      <div className="max-w-7xl mx-auto px-6 md:px-10 py-20 md:py-28 grid lg:grid-cols-2 gap-14 items-center relative">
        <div>
          <Reveal><Eyebrow className="text-forest-300">{t.guarantee.eyebrow}</Eyebrow></Reveal>
          <Reveal delay={0.05}>
            <h2 className="mt-4 font-display text-3xl sm:text-5xl tracking-tight font-semibold text-white leading-[1.05]">
              {t.guarantee.title}
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mt-6 text-forest-200/90 text-lg leading-relaxed max-w-xl">{t.guarantee.text}</p>
          </Reveal>
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          {t.guarantee.points.map((p, i) => (
            <Reveal key={i} delay={0.08 * i}>
              <div className="flex items-start gap-3 rounded-2xl bg-forest-900/70 border border-forest-800/80 p-5 h-full" data-testid={`guarantee-point-${i}`}>
                <BadgeCheck size={20} className="text-forest-300 mt-0.5 shrink-0" />
                <div className="text-sm font-semibold text-forest-50 leading-snug">{p}</div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

const HowItWorks = () => {
  const { t } = useLang();
  const steps = [
    { icon: Sparkles, title: t.how.s1t, desc: t.how.s1d },
    { icon: Clock, title: t.how.s2t, desc: t.how.s2d },
    { icon: MessageCircle, title: t.how.s3t, desc: t.how.s3d },
  ];
  return (
    <section className="py-24 md:py-32 bg-mist" data-testid="how-section">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <SectionTitle eyebrow={t.how.eyebrow} title={t.how.title} />
        <div className="mt-14 grid md:grid-cols-3 gap-6">
          {steps.map((s, i) => (
            <Reveal key={i} delay={0.08 * i}>
              <div className="group rounded-3xl bg-white border border-forest-100 p-8 h-full hover:shadow-xl hover:shadow-forest-900/5 hover:-translate-y-1 transition-all duration-300" data-testid={`how-step-${i}`}>
                <div className="flex items-center justify-between">
                  <div className="w-12 h-12 rounded-2xl bg-mint flex items-center justify-center text-forest-700">
                    <s.icon size={22} />
                  </div>
                  <span className="font-display text-5xl font-light text-forest-100 group-hover:text-forest-200 transition-colors">0{i + 1}</span>
                </div>
                <h3 className="mt-6 font-display text-xl font-semibold text-forest-900 tracking-tight">{s.title}</h3>
                <p className="mt-2.5 text-body text-sm leading-relaxed">{s.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

const CompareTeaser = () => {
  const { t } = useLang();
  const cards = [
    { title: t.compare.full, desc: t.compare.fullDesc, inc: [t.compare.inc1, t.compare.inc1f, t.compare.inc2], dark: true },
    { title: t.compare.half, desc: t.compare.halfDesc, inc: [t.compare.inc1, t.compare.inc1h, t.compare.inc2], dark: false },
  ];
  return (
    <section className="py-24 md:py-32" data-testid="compare-teaser">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <SectionTitle eyebrow={t.compare.eyebrow} title={t.compare.title} sub={t.compare.sub} />
        <div className="mt-14 grid md:grid-cols-2 gap-6">
          {cards.map((c, i) => (
            <Reveal key={i} delay={0.08 * i}>
              <div className={`rounded-[28px] p-9 h-full ${c.dark ? "bg-forest-700 text-white shadow-xl shadow-forest-700/25" : "bg-white border border-forest-150 border-forest-100"}`} data-testid={`package-card-${i}`}>
                <div className="flex items-center gap-2.5">
                  {c.dark ? <Sparkles size={18} className="text-forest-200" /> : <CheckCircle2 size={18} className="text-forest-500" />}
                  <h3 className={`font-display text-2xl font-semibold tracking-tight ${c.dark ? "text-white" : "text-forest-900"}`}>{c.title}</h3>
                </div>
                <p className={`mt-2 text-sm ${c.dark ? "text-forest-100/90" : "text-muted-foreground"}`}>{c.desc}</p>
                <ul className="mt-6 space-y-3">
                  {c.inc.map((f, j) => (
                    <li key={j} className={`flex items-start gap-2.5 text-sm font-medium ${c.dark ? "text-forest-50" : "text-forest-900"}`}>
                      <CheckCircle2 size={17} className={`mt-0.5 shrink-0 ${c.dark ? "text-forest-200" : "text-forest-500"}`} /> {f}
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal delay={0.15}>
          <div className="mt-10 text-center">
            <Link to="/packages" data-testid="compare-table-link" className="group inline-flex items-center gap-2 font-semibold text-forest-700 hover:text-forest-900 transition-colors">
              {t.compare.view}
              <ArrowRight size={17} className="transition-transform duration-300 group-hover:translate-x-1.5" />
            </Link>
          </div>
        </Reveal>
      </div>
    </section>
  );
};

const Testimonials = () => {
  const { t, lang } = useLang();
  const { data: items = [] } = useQuery({ queryKey: ["testimonials"], queryFn: () => api.get("/testimonials").then(r => r.data) });
  return (
    <section className="py-24 md:py-32 bg-mist" data-testid="testimonials-section">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <SectionTitle eyebrow={t.testimonials.eyebrow} title={t.testimonials.title} />
        <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {items.map((r, i) => (
            <Reveal key={r.id} delay={0.06 * i}>
              <div className="rounded-3xl bg-white border border-forest-100 p-7 h-full flex flex-col hover:shadow-lg hover:-translate-y-1 transition-all duration-300" data-testid={`testimonial-${i}`}>
                <div className="flex gap-1">
                  {Array.from({ length: r.rating || 5 }).map((_, s) => (
                    <Star key={s} size={15} className="fill-amber-400 text-amber-400" />
                  ))}
                </div>
                <p className="mt-4 text-sm text-forest-900/85 leading-relaxed flex-1">“{pick(r, "text", lang)}”</p>
                <div className="mt-5 pt-4 border-t border-forest-50">
                  <div className="font-display font-semibold text-forest-900 text-sm">{r.name}</div>
                  <div className="text-xs text-muted-foreground mt-0.5">{pick(r, "location", lang)}</div>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal delay={0.2}>
          <p className="mt-6 text-xs text-muted-foreground/80 text-center">{t.testimonials.sampleNote}</p>
        </Reveal>
      </div>
    </section>
  );
};

const Faq = () => {
  const { t } = useLang();
  return (
    <section id="faq" className="py-24 md:py-32 scroll-mt-16" data-testid="faq-section">
      <div className="max-w-4xl mx-auto px-6 md:px-10">
        <SectionTitle eyebrow={t.faq.eyebrow} title={t.faq.title} align="center" />
        <div className="mt-12">
          <Accordion type="single" collapsible className="w-full">
            {t.faq.items.map((f, i) => (
              <AccordionItem key={i} value={`item-${i}`} className="border-forest-100">
                <AccordionTrigger data-testid={`faq-trigger-${i}`} className="text-left font-display text-lg font-semibold text-forest-900 hover:no-underline hover:text-forest-700 py-6">
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="text-body text-base leading-relaxed pb-6">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </div>
    </section>
  );
};

const CtaBand = () => {
  const { t, lang } = useLang();
  return (
    <section className="px-6 md:px-10 pb-24" data-testid="cta-section">
      <div className="max-w-7xl mx-auto rounded-[40px] bg-forest-700 px-8 md:px-16 py-16 md:py-20 relative overflow-hidden">
        <div className="absolute -top-20 -right-20 w-72 h-72 rounded-full bg-forest-600/60 blur-2xl pointer-events-none" />
        <div className="relative flex flex-col lg:flex-row items-start lg:items-center justify-between gap-10">
          <div className="max-w-xl">
            <Reveal>
              <h2 className="font-display text-3xl sm:text-5xl tracking-tight font-semibold text-white leading-tight">{t.cta.title}</h2>
            </Reveal>
            <Reveal delay={0.08}>
              <p className="mt-4 text-forest-100/90 text-lg">{t.cta.sub}</p>
            </Reveal>
          </div>
          <Reveal delay={0.12}>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link to="/booking" data-testid="cta-book-btn" className="inline-flex items-center justify-center gap-2 rounded-full bg-white px-7 py-4 font-semibold text-forest-900 hover:bg-forest-50 transition-all duration-300 hover:-translate-y-0.5 shadow-lg">
                {t.cta.b1} <ArrowRight size={17} />
              </Link>
              <a href={waLink(t.waMsg.simple[lang])} target="_blank" rel="noopener noreferrer" data-testid="cta-whatsapp-btn" className="inline-flex items-center justify-center gap-2 rounded-full bg-[#25D366] px-7 py-4 font-semibold text-forest-950 hover:brightness-105 transition-all duration-300 hover:-translate-y-0.5 shadow-lg">
                <MessageCircle size={18} /> WhatsApp
              </a>
              <a href={`tel:${PHONE_TEL}`} data-testid="cta-phone-btn" className="inline-flex items-center justify-center gap-2 rounded-full border border-forest-400/60 px-7 py-4 font-semibold text-white hover:bg-forest-800 transition-all duration-300">
                <Phone size={17} /> 5920 0029
              </a>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

export default function Home() {
  const { t } = useLang();
  return (
    <div data-testid="home-page">
      <Hero />
      <Marquee items={t.marquee} />
      <ServicesBento />
      <GuaranteeBand />
      <HowItWorks />
      <CompareTeaser />
      <Testimonials />
      <Faq />
      <CtaBand />
    </div>
  );
}